/**
 * TreeRail — left rail of the flagship estimate-detail screen (width 268px).
 *
 * Holds a line-item search input (filters the grid client-side by
 * description/code), two pinned summary rows ("To Review" / "All Items"),
 * and an "Areas" tree built from the scope's rooms. Selecting a node lifts
 * the selection up so the parent can filter the grid.
 *
 * ⚠ No dollar figures: `ScopeResponseLineItem` carries no price, so rollups
 * show line-item COUNTS only (pricing is computed in Xactimate on export).
 */

import { useState } from 'react';
import type { ScopeResponseRoom } from '../../types';
import { Input } from './ui/input';
import { IconSearch, IconFolder, IconFolderOpen, IconChevr } from './icons';
import { cn } from '../../lib/utils';

/** Sentinel selection ids for the pinned summary rows. */
export const SEL_ALL = '__all';
export const SEL_REVIEW = '__review';

/** A single area node in the rail. Currently a flat list keyed by room name. */
export interface AreaNode {
  id: string;
  label: string;
  count: number;
  children: AreaNode[];
}

/** Props for {@link TreeRail}. */
export interface TreeRailProps {
  /** All rooms in the scope (the tree source). */
  rooms: ScopeResponseRoom[];
  /** Total line-item count across all rooms (drives "All Items"). */
  totalCount: number;
  /** Currently-selected node id (room_name, SEL_ALL, or SEL_REVIEW). */
  selected: string;
  /** Selection change handler. */
  onSelect: (id: string) => void;
  /** Search query (controlled by the parent so the grid can read it too). */
  search: string;
  onSearchChange: (q: string) => void;
}

/**
 * Builds a flat area tree from the scope rooms (one node per room_name).
 *
 * @param rooms - The scope rooms.
 * @returns Area nodes with per-room item counts.
 */
export function buildAreaTree(rooms: ScopeResponseRoom[]): AreaNode[] {
  return rooms.map((room) => ({
    id: room.room_name,
    label: room.room_name || 'Unnamed area',
    count: room.items.length,
    children: [],
  }));
}

/** A pinned summary row at the top of the rail. */
function SummaryRow({
  label,
  count,
  selected,
  bold,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  bold?: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      className={cn(
        'flex items-center justify-between gap-2 h-[var(--tree-h)] px-[6px] rounded-[var(--r-sm)] cursor-pointer',
        bold ? 'font-[560]' : 'font-normal',
        selected
          ? 'bg-[var(--accent-soft)] text-[var(--tx-hi)]'
          : 'text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      <span
        className={cn(
          'min-w-[18px] text-right font-[var(--font-mono)] text-[11px] tabular-nums',
          selected ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-faint)]',
        )}
      >
        {count}
      </span>
    </div>
  );
}

/**
 * Renders the flagship left rail (search + summaries + area tree).
 *
 * @param props - {@link TreeRailProps}.
 * @returns The rail element.
 */
export function TreeRail({
  rooms,
  totalCount,
  selected,
  onSelect,
  search,
  onSearchChange,
}: TreeRailProps) {
  const tree = buildAreaTree(rooms);
  // Areas are leaf nodes (no children today), but the open-state Set keeps
  // the collapsible contract in place for when sub-areas are added.
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex h-full flex-col">
      {/* line-item search */}
      <div className="px-3 pb-2 pt-3">
        <div className="relative">
          <IconSearch className="pointer-events-none absolute left-[10px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[var(--tx-faint)]" />
          <Input
            value={search}
            placeholder="Search line items"
            aria-label="Search line items"
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8 pl-8"
          />
        </div>
      </div>

      {/* pinned summary rows */}
      <div className="px-2">
        <SummaryRow
          label="To Review"
          count={0}
          selected={selected === SEL_REVIEW}
          onClick={() => onSelect(SEL_REVIEW)}
        />
        <SummaryRow
          label="All Items"
          count={totalCount}
          bold
          selected={selected === SEL_ALL}
          onClick={() => onSelect(SEL_ALL)}
        />
      </div>

      <div className="mx-3 my-2 h-px bg-[var(--bd-subtle)]" />

      <div className="px-[18px] pb-[6px] text-[10px] font-semibold uppercase tracking-[.09em] text-[var(--tx-faint)]">
        Areas
      </div>

      {/* area tree */}
      <div className="flex-1 overflow-y-auto px-2">
        {tree.map((node) => {
          const hasKids = node.children.length > 0;
          const isOpen = openIds.has(node.id);
          const isSel = selected === node.id;
          return (
            <div key={node.id}>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === 'Enter' || e.key === ' ') &&
                  (hasKids ? toggle(node.id) : onSelect(node.id))
                }
                onClick={() => (hasKids ? toggle(node.id) : onSelect(node.id))}
                className={cn(
                  'flex items-center gap-[6px] h-[var(--tree-h)] pl-[6px] pr-2 rounded-[var(--r-sm)] cursor-pointer',
                  isSel
                    ? 'bg-[var(--accent-soft)] text-[var(--tx-hi)]'
                    : 'text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
                )}
              >
                <span
                  className={cn(
                    'grid h-[14px] w-[14px] flex-none place-items-center text-[var(--tx-faint)] transition-transform',
                    isOpen && 'rotate-90',
                  )}
                >
                  {hasKids && <IconChevr className="h-3 w-3" />}
                </span>
                {isOpen && hasKids ? (
                  <IconFolderOpen className="h-[15px] w-[15px] flex-none text-[var(--tx-lo)]" />
                ) : (
                  <IconFolder className="h-[15px] w-[15px] flex-none text-[var(--tx-lo)]" />
                )}
                <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  {node.label}
                </span>
                <span
                  className={cn(
                    'min-w-[18px] text-right font-[var(--font-mono)] text-[11px] tabular-nums',
                    isSel ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-faint)]',
                  )}
                >
                  {node.count}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
